import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';
import styled from 'styled-components';

import { buttonShadows } from '../cdl/Button/components/buttonShadows';
import { BaseInput } from '../cdl/Input/components/BaseInput';
import { InputContainer } from '../cdl/Input/components/InputContainer';
import { Text } from '../cdl/Text/Text';
import { translate } from '../common/helpers/translate.helper';
import { IconArrowRight } from '../common/icons/cdl/ArrowRight';
import { theme } from '../common/ui/theme';

const EmptySearchWrapper = styled.div`
    min-height: calc(100vh - 140px);
    display: grid;
    place-items: center;
    transform: translateY(-100px);
`;

const EmptySearchContent = styled.div`
    width: min(100%, 720px);
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const EmptySearchForm = styled.form`
    width: 100%;
    margin-top: ${theme.space[24]};
    margin-bottom: ${theme.space[24]};
`;

const EmptySearchInputContainer = styled(InputContainer)`
    height: 56px;
    padding-right: ${theme.space[12]};
    border-radius: ${theme.radii[8]};
    background-color: ${theme.colors.background.default};
    box-shadow: ${theme.shadows.border.default}, ${theme.shadows.medium};
`;

const SearchButton = styled.button<{ $hasQuery: boolean }>`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border: none;
    border-radius: ${theme.radii[6]};
    padding: 0;
    cursor: pointer;

    ${({ $hasQuery }) =>
        $hasQuery
            ? `
                color: ${theme.colors.foreground.onEmphasis};
                background: linear-gradient(0deg, ${theme.colors.accent.emphasis}, ${theme.colors.accent.emphasis}, ${theme.colors.accent.foreground});
                background-size: 200% 200%;
                background-position: 0% 0%;
                box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorder};
                transition: background-position 100ms ease-out, box-shadow 100ms ease-in-out;

                &:hover {
                    background-position: 100% 100%;
                    box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover}, ${buttonShadows.dropHover};
                }

                &:focus {
                    box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover}, ${buttonShadows.dropHover};
                }

                &:active {
                    background: ${theme.colors.accent.foreground};
                    box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover}, ${buttonShadows.dropHover};
                }
            `
            : `
                color: ${theme.colors.foreground.disabled};
                background: transparent;
                transition: background-color 100ms ease-out;
            `}
`;

const EmptySearchInput = styled(BaseInput)`
    font-size: ${theme.fontSizes[20]};
    line-height: ${theme.lineHeights[26]};
    padding-left: ${theme.space[16]};
`;

export const SuperAdminDashboard = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const hasQuery = query.trim().length > 0;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();

        const nextQuery = query.trim();

        if (!nextQuery) {
            return;
        }

        navigate({ to: '/search', search: { query: nextQuery } });
    };

    return (
        <EmptySearchWrapper>
            <EmptySearchContent>
                <Text as="h1" variant="headline">
                    {translate('searchResult.empty.heading')}
                </Text>

                <EmptySearchForm onSubmit={handleSubmit}>
                    <EmptySearchInputContainer $isFocused={isFocused}>
                        <EmptySearchInput
                            $hasLeadingVisual={false}
                            $hasTrailingVisual
                            autoFocus
                            value={query}
                            placeholder={translate('searchResult.empty.placeholder')}
                            onChange={(event) => setQuery(event.target.value)}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                        />

                        <SearchButton type="submit" $hasQuery={hasQuery}>
                            <IconArrowRight height={16} width={16} />
                        </SearchButton>
                    </EmptySearchInputContainer>
                </EmptySearchForm>

                <Text variant="small" color="foreground.subtle" fontStyle="italic">
                    {translate('searchResult.empty.helper')}
                </Text>
            </EmptySearchContent>
        </EmptySearchWrapper>
    );
};
